import {signal} from "@angular/core"
import {Box2Like} from "@cm/math"
import {deepCopy} from "@cm/utils"
import {
    Operator,
    OperatorCanvasToolboxType,
    OperatorPanelComponentType,
    OperatorProcessingHints,
} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator"
import {OperatorBase} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator-base"
import {OperatorCallback} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator-callback"
import {RecolorPanelComponent} from "@app/textures/texture-editor/operator-stack/operators/recolor/panel/recolor-panel.component"
import * as TextureEditNodes from "@app/textures/texture-editor/texture-edit-nodes"
import {ImageOpNodeBag} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/image-op-node-bag"
import {ImageOpNodeOperatorRecolor} from "@app/textures/texture-editor/operator-stack/operators/recolor/node/node"
import {AnyImageOpResultNode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {TextureType} from "@generated"
import {computeSourceLabStats} from "@app/textures/texture-editor/operator-stack/operators/recolor/helpers/compute-source-lab-stats"
import {getOrComputeCachedUpstreamValue} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/cached-upstream-value"
import {
    computeTargetStatsFromReferenceImage,
    loadReferenceImageFromFile,
    loadReferenceImageFromTextureMap,
    RecolorReferenceImage,
    RecolorReferenceTextureMap,
} from "@app/textures/texture-editor/operator-stack/operators/recolor/helpers/recolor-reference-image"
import {normalizeReferenceRegion} from "@app/textures/texture-editor/operator-stack/operators/recolor/helpers/reference-region-geometry"
import {DataObjectThumbnailService} from "@common/services/data-object-thumbnail/data-object-thumbnail.service"

export class OperatorRecolor extends OperatorBase<TextureEditNodes.OperatorRecolor> {
    readonly panelComponentType: OperatorPanelComponentType = RecolorPanelComponent
    readonly canvasToolbox: OperatorCanvasToolboxType = null

    readonly type = "operator-recolor" as const

    constructor(callback: OperatorCallback, node: TextureEditNodes.OperatorRecolor | null) {
        super(
            callback,
            deepCopy(node) ?? {
                type: "operator-recolor",
                enabled: true,
                ignoreTextureTypes: Object.values(TextureType).filter((textureType) => textureType !== TextureType.Diffuse),
                targetStats: {
                    l: {mean: 0.5, stddev: 0.25},
                    a: {mean: 0, stddev: 0.1},
                    b: {mean: 0, stddev: 0.1},
                },
                luminance: 1,
                colorIntensity: 1,
                fade: 1,
            },
        )
        this._pendingInitialTargetStats = node === null
    }

    // OperatorBase
    override dispose(): void {
        this._disposed = true
        // a running computation may still be using the image, it is released once that has finished
        if (!this.$computingTargetStats()) {
            this._$referenceImage()?.dispose()
        }
        super.dispose()
    }

    // OperatorBase
    override invalidateUpstreamCaches(): void {
        this.node.sourceStatsCache = undefined
    }

    // OperatorBase
    async clone(): Promise<Operator> {
        const clonedOperator = new OperatorRecolor(this.callback, deepCopy(this.node))
        await clonedOperator.init()
        return clonedOperator
    }

    // OperatorBase
    protected override async createImageOpGraph(
        sourceImage: AnyImageOpResultNode,
        hints: OperatorProcessingHints,
        imageOpNodeBag: ImageOpNodeBag,
    ): Promise<AnyImageOpResultNode> {
        // sourceStats is only eagerly computed/cached for interactive preview — for baking (final)
        // it's left undefined and ImageOpNodeOperatorRecolor computes it live instead, folded into
        // whatever imgproc evaluation is already running rather than forcing a separate
        // client-side download+evaluate of every texture type's source image on every submit.
        let sourceStats: TextureEditNodes.OperatorRecolor["targetStats"] | undefined
        if (hints.mode === "preview") {
            const {value, cache} = await getOrComputeCachedUpstreamValue(hints.textureType, this.node.sourceStatsCache, () =>
                computeSourceLabStats(this.callback.imageOpSystem, sourceImage),
            )
            sourceStats = value
            this.node.sourceStatsCache = cache
        }

        if (sourceStats && this._pendingInitialTargetStats) {
            this._pendingInitialTargetStats = false
            this.setTargetStats(deepCopy(sourceStats))
        }

        return imageOpNodeBag.getNode("recolor", ImageOpNodeOperatorRecolor, {
            sourceImage,
            targetStats: this.node.targetStats,
            sourceStats,
            luminance: this.node.luminance,
            colorIntensity: this.node.colorIntensity,
            fade: this.node.fade,
        })
    }

    private readonly _$targetMeanL = signal(this.node.targetStats.l.mean)
    readonly $targetMeanL = this._$targetMeanL.asReadonly()
    setTargetMeanL(value: number): void {
        if (this.node.targetStats.l.mean === value) {
            return
        }
        this.node.targetStats.l.mean = value
        this._$targetMeanL.set(value)
        this.markEdited()
        this.requestEval()
    }

    private readonly _$targetStddevL = signal(this.node.targetStats.l.stddev)
    readonly $targetStddevL = this._$targetStddevL.asReadonly()
    setTargetStddevL(value: number): void {
        if (this.node.targetStats.l.stddev === value) {
            return
        }
        this.node.targetStats.l.stddev = value
        this._$targetStddevL.set(value)
        this.markEdited()
        this.requestEval()
    }

    private readonly _$targetMeanA = signal(this.node.targetStats.a.mean)
    readonly $targetMeanA = this._$targetMeanA.asReadonly()
    setTargetMeanA(value: number): void {
        if (this.node.targetStats.a.mean === value) {
            return
        }
        this.node.targetStats.a.mean = value
        this._$targetMeanA.set(value)
        this.markEdited()
        this.requestEval()
    }

    private readonly _$targetStddevA = signal(this.node.targetStats.a.stddev)
    readonly $targetStddevA = this._$targetStddevA.asReadonly()
    setTargetStddevA(value: number): void {
        if (this.node.targetStats.a.stddev === value) {
            return
        }
        this.node.targetStats.a.stddev = value
        this._$targetStddevA.set(value)
        this.markEdited()
        this.requestEval()
    }

    private readonly _$targetMeanB = signal(this.node.targetStats.b.mean)
    readonly $targetMeanB = this._$targetMeanB.asReadonly()
    setTargetMeanB(value: number): void {
        if (this.node.targetStats.b.mean === value) {
            return
        }
        this.node.targetStats.b.mean = value
        this._$targetMeanB.set(value)
        this.markEdited()
        this.requestEval()
    }

    private readonly _$targetStddevB = signal(this.node.targetStats.b.stddev)
    readonly $targetStddevB = this._$targetStddevB.asReadonly()
    setTargetStddevB(value: number): void {
        if (this.node.targetStats.b.stddev === value) {
            return
        }
        this.node.targetStats.b.stddev = value
        this._$targetStddevB.set(value)
        this.markEdited()
        this.requestEval()
    }

    // Best-effort: only populated once a preview eval has run for this texture type (and cleared by
    // invalidateUpstreamCaches() whenever anything upstream changes), so this may be undefined.
    getSourceStatsForCurrentTextureType(): TextureEditNodes.OperatorRecolor["targetStats"] | undefined {
        const textureType = this.callback.selectedTextureType
        return this.node.sourceStatsCache?.[textureType]
    }

    // The texture type a reference texture map defaults to: the viewed one if this operator targets it,
    // otherwise the first one it does target. Reads the ignore list directly, as shouldApplyTo() is also
    // false for all types while the operator is disabled.
    get defaultReferenceTextureType(): TextureType {
        const ignoredTextureTypes = this.node.ignoreTextureTypes ?? []
        const selectedTextureType = this.callback.selectedTextureType
        if (!ignoredTextureTypes.includes(selectedTextureType)) {
            return selectedTextureType
        }
        return this.callback.revision.availableTextureTypes.find((textureType) => !ignoredTextureTypes.includes(textureType)) ?? TextureType.Diffuse
    }

    private readonly _$referenceImage = signal<RecolorReferenceImage | undefined>(undefined)
    readonly $referenceImage = this._$referenceImage.asReadonly()

    private readonly _$referenceRegion = signal<Box2Like | undefined>(undefined)
    readonly $referenceRegion = this._$referenceRegion.asReadonly() // undefined for the entire image

    // the persisted reference could not be loaded anymore, e.g. because its revision has been deleted
    private readonly _$referenceUnavailable = signal(false)
    readonly $referenceUnavailable = this._$referenceUnavailable.asReadonly()

    // Lives here rather than in the panel, so that a panel re-opened during a computation still knows
    // about it and can't start a second one.
    private readonly _$computingTargetStats = signal(false)
    readonly $computingTargetStats = this._$computingTargetStats.asReadonly()

    async runTargetStatsComputation(compute: () => Promise<void>): Promise<void> {
        if (this.$computingTargetStats()) {
            return
        }
        this._$computingTargetStats.set(true)
        try {
            await compute()
        } finally {
            this._$computingTargetStats.set(false)
            if (this._disposed) {
                this._$referenceImage()?.dispose()
            }
        }
    }

    setReferenceFromFile(file: File): Promise<void> {
        return this.setReference(() => loadReferenceImageFromFile(file, this.callback.texturesApi))
    }

    setReferenceFromTextureMap(textureMap: RecolorReferenceTextureMap): Promise<void> {
        return this.setReference(() => this.loadTextureMap(textureMap))
    }

    // Everything is only committed once the statistics are there, so a failure at any point leaves the
    // current reference and statistics as they are.
    private setReference(load: () => Promise<RecolorReferenceImage>): Promise<void> {
        return this.runTargetStatsComputation(async () => {
            const image = await load()
            try {
                const targetStats = await computeTargetStatsFromReferenceImage(this.callback.imageOpSystem, image)
                if (this._disposed) {
                    image.dispose()
                    return
                }
                this._$referenceImage()?.dispose()
                this._$referenceImage.set(image)
                this._$referenceUnavailable.set(false)
                this.commitReferenceRegion(undefined)
                this.setTargetStats(targetStats)
            } catch (error) {
                image.dispose()
                throw error
            }
        })
    }

    setReferenceRegion(region: Box2Like | undefined): Promise<void> {
        return this.runTargetStatsComputation(async () => {
            const image = this.$referenceImage()
            if (!image) {
                return
            }
            // shown right away, as the region selector would otherwise jump back to the old region until the
            // statistics are there. Only persisted once they are, and undone if they can't be computed.
            const previousRegion = this.$referenceRegion()
            this._$referenceRegion.set(region)
            try {
                const targetStats = await computeTargetStatsFromReferenceImage(this.callback.imageOpSystem, image, region)
                if (!this._disposed) {
                    this.commitReferenceRegion(region)
                    this.setTargetStats(targetStats)
                }
            } catch (error) {
                this._$referenceRegion.set(previousRegion)
                throw error
            }
        })
    }

    private commitReferenceRegion(region: Box2Like | undefined): void {
        this._$referenceRegion.set(region)
        // uploads can't be persisted, so only a texture map ends up on the node
        const textureMap = this.$referenceImage()?.textureMap
        if (!textureMap) {
            delete this.node.referenceTextureMap
        } else if (region) {
            this.node.referenceTextureMap = {...textureMap, region: {type: "region", ...region}}
        } else {
            this.node.referenceTextureMap = {...textureMap}
        }
    }

    // for when targetStats stop coming from a reference image, so that no stale one is shown
    clearReference(): void {
        this._$referenceImage()?.dispose()
        this._$referenceImage.set(undefined)
        this._$referenceRegion.set(undefined)
        this._$referenceUnavailable.set(false)
        delete this.node.referenceTextureMap
    }

    // Brings back a persisted reference, without ever touching targetStats: those stay frozen until the
    // user actually changes something, even if the referenced map has been re-baked in the meantime.
    async restorePersistedReference(): Promise<void> {
        const persisted = this.node.referenceTextureMap
        if (!persisted || this.$referenceImage()) {
            return
        }
        try {
            const image = await this.loadTextureMap({textureSetRevisionId: persisted.textureSetRevisionId, textureType: persisted.textureType})
            // the user may have picked something else in the meantime
            if (this._disposed || this.node.referenceTextureMap !== persisted) {
                image.dispose()
                return
            }
            this._$referenceImage.set(image)
            this._$referenceRegion.set(persisted.region && normalizeReferenceRegion(persisted.region, image))
        } catch {
            if (this.node.referenceTextureMap === persisted) {
                this._$referenceUnavailable.set(true)
            }
        }
    }

    private loadTextureMap(textureMap: RecolorReferenceTextureMap): Promise<RecolorReferenceImage> {
        return loadReferenceImageFromTextureMap(textureMap, this.callback.texturesApi, this.callback.injector.get(DataObjectThumbnailService))
    }

    setTargetStats(targetStats: TextureEditNodes.OperatorRecolor["targetStats"]): void {
        // whatever is set here must not be overwritten by the initial seed of a later preview eval, which
        // hasn't happened yet if the operator doesn't apply to the texture type viewed so far
        this._pendingInitialTargetStats = false
        this.node.targetStats = targetStats
        this._$targetMeanL.set(targetStats.l.mean)
        this._$targetStddevL.set(targetStats.l.stddev)
        this._$targetMeanA.set(targetStats.a.mean)
        this._$targetStddevA.set(targetStats.a.stddev)
        this._$targetMeanB.set(targetStats.b.mean)
        this._$targetStddevB.set(targetStats.b.stddev)
        this.markEdited()
        this.requestEval()
    }

    private readonly _$luminance = signal(this.node.luminance)
    readonly $luminance = this._$luminance.asReadonly()
    setLuminance(value: number): void {
        if (this.node.luminance === value) {
            return
        }
        this.node.luminance = value
        this._$luminance.set(value)
        this.markEdited()
        this.requestEval()
    }

    private readonly _$colorIntensity = signal(this.node.colorIntensity)
    readonly $colorIntensity = this._$colorIntensity.asReadonly()
    setColorIntensity(value: number): void {
        if (this.node.colorIntensity === value) {
            return
        }
        this.node.colorIntensity = value
        this._$colorIntensity.set(value)
        this.markEdited()
        this.requestEval()
    }

    private readonly _$fade = signal(this.node.fade)
    readonly $fade = this._$fade.asReadonly()
    setFade(value: number): void {
        if (this.node.fade === value) {
            return
        }
        this.node.fade = value
        this._$fade.set(value)
        this.markEdited()
        this.requestEval()
    }

    // True for a freshly created operator (no saved node yet) until createImageOpGraph() has had a chance to
    // seed targetStats from the operator's own source stats, so recoloring is a no-op until the user picks a
    // reference image.
    private _pendingInitialTargetStats: boolean

    private _disposed = false
}
